import { SegmentedControl } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Select } from 'src/design/atoms/Select';
import { IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DailyDto, JobDto, PostFilterDto } from 'src/api/FiretrolApiClient';
import { useDailys } from 'src/api/queries/dailys/useDailys';
import { usePostSearchForOperations } from 'src/api/queries/posts/usePostSearchForOperations';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { Header } from 'src/design/organisms/Header';
import { useJobs } from 'src/hooks/useJobs';
import { DailyTable } from 'src/routes/Jobs/Dailys/DailyTable';
import { ActivityFeedCard } from 'src/routes/Jobs/Summary/ActivityFeedCard';
import { ChangesSummary } from 'src/routes/Jobs/Summary/ChangesSummary';
import { RFISummary } from 'src/routes/Jobs/Summary/RFISummary';

const postFilter: PostFilterDto = {
  visibility: 'all',
  resolved: 'unresolved',
  assignedToMe: true,
  createdByMe: true,
  constraint: false,
  critical: false,
  ctc: false,
  cost: false,
  assigned: false,
};

const EMPTY_DAILYS: DailyDto[] = [];

export const Operations = () => {
  const navigate = useNavigate();
  const location: any = useLocation();
  const { jobs } = useJobs();
  const [selectedJob, setSelectedJob] = React.useState<JobDto | null>(null);
  const [filter, setFilter] = React.useState('week');

  React.useEffect(() => {
    if (location.state?.job) {
      setSelectedJob(location.state.job);
    }
  }, []);

  const { data: posts = null, isLoading: postsLoading } =
    usePostSearchForOperations(selectedJob?.id, postFilter);
  const { data: dailys = EMPTY_DAILYS, isLoading: dailysLoading } = useDailys(
    { jobId: selectedJob?.id },
    { enabled: !!selectedJob },
  );
  const loading = postsLoading || dailysLoading;

  const dailyComplete = React.useMemo(
    () => dailys.some((d) => moment(d.date).isSame(moment(), 'day')),
    [dailys],
  );

  const jobData = React.useMemo(() => {
    return jobs.map((job) => ({
      value: String(job.id),
      label: `${job.jobNumber} - ${job.name}`,
    }));
  }, [jobs]);

  const filteredDailys = React.useMemo(() => {
    if (filter === 'all') return dailys;
    if (filter === 'month')
      return dailys.filter((d) => moment(d.date).isSame(moment(), filter));
    if (filter === 'week')
      return dailys.filter((d) => moment(d.date).isSame(moment(), filter));
    return dailys;
  }, [filter, dailys]);

  return (
    <Box pos="relative">
      <Header
        title="Operations"
        breadcrumbProps={{
          items: [{ text: 'Operations' }],
        }}
        helpText="Operations dashboard for managing job operations, dailys, and activity tracking."
      />

      <Stack gap="sm">
        <LoadingOverlay visible={loading} />

        <Group justify="space-between">
          <div className="width-400">
            <Select
              placeholder="Select Job..."
              data={jobData}
              value={selectedJob ? String(selectedJob.id) : null}
              onChange={(selectedValue) => {
                if (selectedValue) {
                  const selected =
                    jobs.find((j) => String(j.id) === selectedValue) || null;
                  setSelectedJob(selected);
                } else {
                  setSelectedJob(null);
                }
              }}
              clearable={true}
              searchable
            />
          </div>

          <CreateButton
            onClick={() =>
              navigate('/operations/daily', { state: { job: selectedJob } })
            }
          >
            Create Daily
          </CreateButton>
        </Group>

        {selectedJob && (
          <Group>
            {(selectedJob?.hyperlinks || []).map((h) => {
              return (
                <LinkButton key={h.id || nanoid()} to={h.url} target="_blank">
                  {h.title}
                </LinkButton>
              );
            })}
          </Group>
        )}

        {!selectedJob && (
          <Alert
            color="blue"
            variant="light"
            title="Select Job"
            icon={<IconAlertCircle />}
          >
            Select a job to view the dashboard.
          </Alert>
        )}

        {selectedJob && (
          <>
            {!dailyComplete && (
              <Alert
                color="yellow"
                variant="light"
                title="Daily Missing"
                icon={<IconAlertTriangle />}
              >
                <Stack gap="xs">
                  <Text c="black">
                    Please complete your daily report for today.
                  </Text>
                  <CreateButton
                    w="fit-content"
                    onClick={() =>
                      navigate('/operations/daily', {
                        state: { job: selectedJob },
                      })
                    }
                  >
                    Create Daily
                  </CreateButton>
                </Stack>
              </Alert>
            )}

            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Paper h="fit-content">
                <Stack gap="xs">
                  <Title order={5}>Dailys</Title>

                  <SegmentedControl
                    size="sm"
                    color="blue"
                    value={filter}
                    onChange={setFilter}
                    data={[
                      { label: 'Current Week', value: 'week' },
                      { label: 'Current Month', value: 'month' },
                      { label: 'All Time', value: 'all' },
                    ]}
                  />

                  <DailyTable
                    dailys={filteredDailys}
                    loading={false}
                    openInNewTab
                  />
                </Stack>
              </Paper>

              <Stack gap="md">
                <ActivityFeedCard
                  title="Open Items Assigned to You"
                  items={posts?.data || []}
                  openInNewTab
                />

                <ChangesSummary jobId={selectedJob.id} />

                <RFISummary jobId={selectedJob.id} />
              </Stack>
            </SimpleGrid>
          </>
        )}
      </Stack>
    </Box>
  );
};
